"use client";

import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import {
  Address,
  type Chain,
  type HttpTransport,
  type PrivateKeyAccount,
  type TypedDataParameter,
  type WalletClient,
} from "viem";

import { errorMessages } from "#/config/errorMessages";
import { type ChainId } from "#/config/web3/contractAddresses";
import { useWalletClient } from "#/hooks/useWalletClient";
import { useAccount } from "#/store/wallet/account/account";
import { getPublicClient } from "#/utils/getPublicClient";
import { getSignatureDeadline } from "#/utils/getSignatureDeadline";
import { EIP712Domain } from "#/utils/signature";

import { cancelRFQRequest } from "../kyan.service";
import { RFQCancelRequest } from "../kyan.types";
import { defaultApiResponseHandler } from "../kyan.utils";
import { useTradingSession } from "./useTradingSession";

const CancelRFQRequestType: TypedDataParameter[] = [
  { name: "deadline", type: "uint256" },
  { name: "taker", type: "address" },
  { name: "orderId", type: "string" },
];

const signCancelRFQRequest = async (
  request: Omit<RFQCancelRequest, "signature" | "signature_deadline">,
  walletClient: WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
): Promise<RFQCancelRequest> => {
  const account = walletClient.account.address;
  const signature_deadline = getSignatureDeadline();

  try {
    const signature = await walletClient.signTypedData({
      account,
      domain: EIP712Domain[walletClient.chain.id as ChainId],
      types: { CancelRFQRequestType },
      primaryType: "CancelRFQRequestType",
      message: {
        deadline: signature_deadline,
        taker: request.taker,
        orderId: request.order_id,
      },
    });

    return {
      ...request,
      signature_deadline,
      signature,
    };
  } catch (err) {
    if (String(err).includes("chainId should be same as current chainId")) {
      throw new Error(
        `Wrong chainId on signature: ${await walletClient.getChainId()}`,
      );
    }
    if (String(err).includes("UserRejectedRequestError")) {
      throw new Error("Cancel RFQ request signature rejected");
    }

    throw new Error("Error signing cancel RFQ request");
  }
};

const validateCancelRFQRequestSignature = async (
  request: RFQCancelRequest,
  chainId: ChainId,
): Promise<boolean> => {
  const publicClient = getPublicClient(chainId);

  return publicClient.verifyTypedData({
    address: request.taker as Address,
    domain: EIP712Domain[chainId],
    types: { CancelRFQRequestType },
    primaryType: "CancelRFQRequestType",
    message: {
      deadline: request.signature_deadline,
      taker: request.taker,
      orderId: request.order_id,
    },
    signature: request.signature,
  });
};

export const useCancelRFQRequest = (
  options?: UseMutationOptions<string, Error, { order_id: string }>,
) => {
  const { account } = useAccount();
  const { walletClient } = useWalletClient();
  const { shouldUseSessionHash } = useTradingSession();

  return useMutation({
    ...options,
    mutationFn: async (request: { order_id: string }) => {
      if (!walletClient) {
        throw new Error(errorMessages.WALLET_CLIENT_UNAVAILABLE);
      }

      const requestData = {
        taker: account!,
        order_id: request.order_id,
      };

      if (shouldUseSessionHash) {
        return await defaultApiResponseHandler({
          serviceName: "Cancel RFQ Request",
          payload: {
            data: requestData,
            includeSessionHash: true,
          },
          service: cancelRFQRequest,
        });
      } else {
        const signedData = await signCancelRFQRequest(
          requestData,
          walletClient as WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
        );

        const isSignatureValid = await validateCancelRFQRequestSignature(
          signedData,
          walletClient.chain?.id as ChainId,
        );

        return await defaultApiResponseHandler({
          serviceName: "Cancel RFQ Request",
          payload: { data: signedData },
          service: cancelRFQRequest,
          validation: [isSignatureValid],
        });
      }
    },
  });
};
